import { ActionIcon, Badge, Box, Card, CopyButton, Group, Loader, NavLink, SegmentedControl, Stack, Table, Text, Textarea, TextInput, Title, Tooltip, UnstyledButton } from "@mantine/core";
import { type KeyboardEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import { type Fact, FactTable } from "../../common/fact-table";
import { graphemes } from "../../common/graphemes";
import { ALWAYS, keepFocus, useRovingFocus } from "../../common/roving-focus";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { UtilityTitle } from "../../common/utility-title";
import { IconCheck, IconChevronDown, IconChevronUp, IconCopy, IconSearch } from "../../icons";
import { categoryName, type Character, codePoint, isInvisible, placeholder, readCharacters } from "./characters";
import { encodings, escapes } from "./encode";
import { DEFAULT_GROUP, filterSections, type Key, type Keys, keysLabel, keysOf, readSearch, type Section, typed } from "./keys";
import { useNames } from "./names";
import { normalisations, type Normalised } from "./normalise";
import { readPoints, writePoints } from "./points";
import { type Finding, findings } from "./risks";
import { MAX_ROWS, type Mode, MODE_OPTIONS, MODES, pickAt, pickGroup, pickMode, pickValue } from "./settings";

export default function Unicode() {
  const initialState = useInitialHashState<{
    mode?: string;
    value?: string;
    at?: number;
    group?: string;
  }>();

  const initialMode = pickMode(initialState?.mode);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [value, setValue] = useState(() => pickValue(initialState?.value, initialMode));
  const [at, setAt] = useState(() => pickAt(initialState?.at));
  // The group of characters the keyboard is showing, and null for a keyboard nobody has opened.
  const [group, setGroup] = useState(() => pickGroup(initialState?.group));
  // How the group was found rather than which one was found, so it does not travel in the link: what a reader chose
  // to see is the group, and a list narrowed on the way to it is this screen's business in the way a pane width is.
  const [query, setQuery] = useState("");

  const box = useRef<HTMLTextAreaElement>(null);
  // Where a key types: the selection while the box has it, and where the box last left it once it has not.
  const caret = useRef<[number, number] | null>(null);
  const keyed = useRef(false);

  const reading = useMemo(() => mode === "points" ? readPoints(value) : { text: value, error: "" }, [value, mode]);
  const characters = useMemo(() => readCharacters(reading.text), [reading.text]);
  const found = useMemo(() => findings(characters, reading.text), [characters, reading.text]);
  const forms = useMemo(() => normalisations(reading.text), [reading.text]);
  const search = useMemo(() => readSearch(query), [query]);
  const sections = useMemo(() => filterSections(search), [search]);
  const keys = useMemo(() => group === null ? EMPTY_KEYS : keysOf(group, search), [group, search]);
  // The names arrive a file at a time, so this is the only thing on the page that is not there the moment it renders.
  // What is asked for is the text's own and the keys the standard names — an emoji carries its own and needs none.
  const { nameOf, reading: naming } = useNames([
    ...characters.map(({ code }) => code),
    ...keys.keys.flatMap(({ code, name }) => code !== null && name === "" ? [code] : []),
  ]);

  // The character the cards under the table are about; a link made before an edit can name one past the end of it.
  const current = Math.min(at, characters.length - 1);
  const selected = characters[current];
  const name = selected ? nameOf(selected.code) : "";

  useRegisterShareState(() => ({
    mode,
    value,
    // The row being shown rather than the one that was asked for, and never the first, which every text opens on:
    // a link made before an edit can name a character past the end, and what it should carry is what is on screen.
    at: current > 0 ? current : undefined,
    // One field for the keyboard rather than two: a closed keyboard is showing no group, and says so by saying nothing.
    group: group ?? undefined,
  }));

  // The two modes are one box read two ways, so the switch rewrites what is in it rather than emptying it: the
  // characters go on being the same characters, spelled as themselves or as the numbers they are stored under.
  const onMode = (next: string) => {
    const picked = pickMode(next);
    setMode(picked);
    setValue(picked === "points" ? writePoints(reading.text) : reading.text);
  };

  // A key types into the box rather than onto the end of it, so what the caret is sitting in is what it lands in.
  // Nothing here moves the focus: a click leaves it in the box, where the caret now is, and a key pressed from the
  // keyboard leaves it on the key, so the next character is one more press rather than a walk back to the palette.
  const onKey = (key: Key) => {
    const [start, end] = selection(box.current, caret.current, value.length);
    const spelled = typed(key, mode, value.slice(0, start));
    setValue(value.slice(0, start) + spelled + value.slice(end));
    caret.current = [start + spelled.length, start + spelled.length];
    keyed.current = true;
  };

  // The caret is put where the character went on the render that wrote it, and never on any other: setting it every
  // time would take it off what somebody is typing. The box is focused only where nothing else is.
  useLayoutEffect(() => {
    if (!keyed.current) return;
    keyed.current = false;
    const [start, end] = caret.current ?? [value.length, value.length];
    box.current?.setSelectionRange(start, end);
    if (document.activeElement === document.body) box.current?.focus();
  });

  return (
    <Stack gap="md">
      <UtilityTitle
        directory="unicode"
        publications="the Unicode Character Database"
        control={
          <SegmentedControl value={mode} onChange={onMode} aria-label="How the box is read" data={MODE_OPTIONS} />
        }
      >
        {MODES[mode].title}
      </UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Textarea
          ref={box}
          label={MODES[mode].field}
          description={MODES[mode].hint}
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          // Where the caret was when the box last had it, which is where a key typed from the keyboard puts its
          // character: the box has lost the focus by then, and a browser reports the selection of a box that has.
          onBlur={(event) => {
            caret.current = [event.currentTarget.selectionStart, event.currentTarget.selectionEnd];
          }}
          error={reading.error || undefined}
          autosize
          minRows={3}
          maxRows={10}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          styles={{ input: { fontFamily: "monospace" } }}
        />
      </Card>

      <Card withBorder shadow="sm" radius="md" data-keyboard>
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap">
            <Title order={4}>Keyboard</Title>
            <Tooltip label={group === null ? "Show the keyboard" : "Hide the keyboard"} withArrow position="left">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setGroup(group === null ? DEFAULT_GROUP : null)}
                aria-label={group === null ? "Show the keyboard" : "Hide the keyboard"}
                aria-expanded={group !== null}
              >
                {group === null ? <IconChevronDown size="1.2rem" /> : <IconChevronUp size="1.2rem" />}
              </ActionIcon>
            </Tooltip>
          </Group>
          {group !== null && (
            <Box className="keyboard-panes">
              <GroupMenu sections={sections} group={group} onGroup={setGroup} query={query} onQuery={setQuery} />
              <Stack gap="xs" className="keyboard-keys">
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Text size="sm" fw={500} truncate>{group}</Text>
                  <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>{keysLabel(keys)}</Text>
                </Group>
                <Keys keys={keys.keys} sifted={keys.sifted} nameOf={nameOf} onKey={onKey} />
              </Stack>
            </Box>
          )}
        </Stack>
      </Card>

      {found.length > 0 && (
        <Card withBorder shadow="sm" radius="md" data-findings>
          <Stack gap="sm">
            <Title order={4}>Findings</Title>
            {found.map((finding) => <FindingRow key={finding.kind} finding={finding} />)}
          </Stack>
        </Card>
      )}

      <Box className="card-columns">
        <Card withBorder shadow="sm" radius="md" data-counts>
          <Stack gap="xs">
            <Title order={4}>Text</Title>
            <FactTable rows={counts(reading.text, characters)} />
          </Stack>
        </Card>
        <Card withBorder shadow="sm" radius="md" data-normalisation>
          <Stack gap="xs">
            <Title order={4}>Normalisation</Title>
            <Forms forms={forms} />
          </Stack>
        </Card>
      </Box>

      <Card withBorder shadow="sm" radius="md" data-characters>
        <Stack gap="xs">
          <Group justify="space-between">
            <Title order={4}>Characters</Title>
            <Group gap="xs">
              {naming && <Loader size="xs" />}
              <Text size="sm" c="dimmed">
                {characters.length > MAX_ROWS
                  ? `${MAX_ROWS} of ${characters.length} shown`
                  : `${characters.length} code point${characters.length === 1 ? "" : "s"}`}
              </Text>
            </Group>
          </Group>
          <Characters characters={characters} nameOf={nameOf} at={current} onPick={setAt} />
        </Stack>
      </Card>

      {selected && (
        <>
          <Card withBorder shadow="sm" radius="md" data-character>
            <Group align="flex-start" wrap="nowrap" gap="lg">
              <Glyph character={selected} />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <FactTable rows={identity(selected, name)} />
              </Box>
            </Group>
          </Card>

          <Box className="card-columns">
            <Card withBorder shadow="sm" radius="md" data-encodings>
              <Stack gap="xs">
                <Title order={4}>Encodings</Title>
                <FactTable rows={encodings(selected)} />
              </Stack>
            </Card>
            <Card withBorder shadow="sm" radius="md" data-escapes>
              <Stack gap="xs">
                <Title order={4}>Escapes</Title>
                <FactTable rows={escapes(selected, name)} />
              </Stack>
            </Card>
          </Box>
        </>
      )}
    </Stack>
  );
}

// The list of what the standard arranges characters into, beside the keys rather than in a dropdown over them: three
// hundred and forty-seven blocks and ten emoji groups is a list somebody searches and scrolls, and a menu that shuts
// itself the moment anything is picked is one nobody can walk down. It is the site's own navbar at the scale of a
// card — the same links and the same lit state, denser and with no icons, since a block has no picture to give it —
// and it is one tab stop through the same hook the keys beside it are one through.
function GroupMenu(
  { sections, group, onGroup, query, onQuery }: {
    sections: Section[];
    group: string;
    onGroup: (group: string) => void;
    query: string;
    onQuery: (query: string) => void;
  },
) {
  const menu = useRef<HTMLDivElement>(null);
  // Where each section starts in the one list the arrows walk, a section being a heading rather than a stop of its own.
  const offsets = useMemo(() => {
    let at = 0;
    return sections.map(({ groups }) => {
      const start = at;
      at += groups.length;
      return start;
    });
  }, [sections]);
  const count = sections.reduce((total, { groups }) => total + groups.length, 0);
  const roving = useRovingFocus(count, 1, ALWAYS);

  // A link can arrive on a block three hundred deep, so the list opens on the one it is showing rather than at the
  // top. `nearest` is what makes that only ever the list's own scrolling: a group already in view moves nothing.
  useLayoutEffect(() => {
    menu.current?.querySelector("[aria-selected='true']")?.scrollIntoView({ block: "nearest" });
  }, [group]);

  return (
    <Box ref={menu} className="keyboard-menu">
      {/* Sticky, since a list this long is scrolled and the box that shortens it is no use at the top of the scroll. */}
      <Box className="keyboard-search">
        <TextInput
          value={query}
          onChange={(event) => onQuery(event.currentTarget.value)}
          aria-label="Search"
          placeholder="A name or a code point"
          leftSection={<IconSearch size="0.9rem" />}
          size="xs"
        />
      </Box>
      <Box role="listbox" aria-label="Characters" onKeyDown={roving.handleKeyDown}>
        {sections.map((section, index) => (
          <Box key={section.name} role="group" aria-label={section.name}>
            <Text className="keyboard-section" size="xs" c="dimmed" fw={700} tt="uppercase">{section.name}</Text>
            {section.groups.map((name, at) => (
              <NavLink
                key={name}
                component="button"
                type="button"
                className="keyboard-option"
                role="option"
                aria-selected={name === group}
                active={name === group}
                label={name}
                onClick={() => onGroup(name)}
                {...roving.itemProps((offsets[index] ?? 0) + at)}
              />
            ))}
          </Box>
        ))}
        {count === 0 && <Text size="sm" c="dimmed" p="xs">Nothing is called that.</Text>}
      </Box>
    </Box>
  );
}

// Every character of a block, each one a key that types itself into the box. It is a toolbar and not two hundred and
// fifty-six tab stops: one Tab reaches the grid and the arrow keys walk it, which is what `/calculator`'s pads do and
// the reason that hook is in `src/common`. The columns are the grid's own, so how many the arrows step by is read off
// what the browser laid out rather than fixed here — a palette that wrapped to five would otherwise move by six.
function Keys(
  { keys, sifted, nameOf, onKey }: { keys: Key[]; sifted: boolean; nameOf: (code: number) => string; onKey: Typing },
) {
  const grid = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);
  const roving = useRovingFocus(keys.length, columns, ALWAYS);

  // Measured rather than declared, and measured again when the region changes: an auto-filled grid decides its own
  // columns, and the arrows have to step by however many the browser actually laid out.
  useLayoutEffect(() => {
    const laid = grid.current;
    if (!laid) return;
    const measure = () => setColumns(columnsOf(laid));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(laid);
    return () => observer.disconnect();
  }, [keys]);

  if (keys.length === 0) {
    const nothing = sifted ? "Nothing in this group matches." : "Nothing to type from this block.";
    return <Text size="sm" c="dimmed">{nothing}</Text>;
  }

  return (
    <Box
      ref={grid}
      className="character-keys"
      role="toolbar"
      aria-label="Characters to type"
      onKeyDown={roving.handleKeyDown}
    >
      {keys.map((key, index) => (
        <UnstyledButton
          key={key.text}
          className="character-key"
          data-invisible={key.invisible || undefined}
          data-key={writePoints(key.text)}
          onClick={() => onKey(key)}
          // The click types without taking the focus, so the caret stays in the box the character just went into.
          onMouseDown={keepFocus}
          title={keyName(key, nameOf)}
          aria-label={keyName(key, nameOf)}
          {...roving.itemProps(index)}
        >
          {key.label}
        </UnstyledButton>
      ))}
    </Box>
  );
}

// How many keys the browser put on a row, which is one row's worth of children sharing the first one's top edge.
function columnsOf(grid: HTMLDivElement | null): number {
  const laid = [...grid?.children ?? []].filter((child): child is HTMLElement => child instanceof HTMLElement);
  const wrapped = laid.findIndex((child) => child.offsetTop > (laid[0]?.offsetTop ?? 0));
  return wrapped === -1 ? Math.max(laid.length, 1) : wrapped;
}

// A finding is the one thing on this page that is a judgement rather than a reading, so it says what it found, what
// that means, and which code points it is about — the last of those being what makes it checkable rather than trusted.
function FindingRow({ finding }: { finding: Finding }) {
  return (
    <Group gap="xs" align="flex-start" wrap="nowrap" data-finding={finding.kind}>
      <Badge color={finding.serious ? "orange" : "gray"} variant="light" tt="none">{finding.label}</Badge>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm">{finding.detail}</Text>
        {finding.codes.length > 0 && (
          <Text size="sm" c="dimmed" ff="monospace" style={{ overflowWrap: "anywhere" }}>
            {finding.codes.slice(0, MAX_CODES).map(codePoint).join(" ")}
            {finding.codes.length > MAX_CODES && ` and ${finding.codes.length - MAX_CODES} more`}
          </Text>
        )}
      </Box>
    </Group>
  );
}

// The four forms with the text each one makes, and a word for the one the text is already in — a row that repeats
// what is in the box above it says nothing, and a reader needs telling which of the four that was.
function Forms({ forms }: { forms: Normalised[] }) {
  return (
    <Table verticalSpacing={6} horizontalSpacing="xs" withRowBorders={false}>
      <Table.Tbody>
        {forms.map((form) => (
          <Table.Tr key={form.form} data-form={form.form}>
            <Table.Td w="1%" style={{ whiteSpace: "nowrap" }}>
              <Tooltip label={form.hint} withArrow multiline w={260}>
                <Text size="sm" c="dimmed">{form.form}</Text>
              </Tooltip>
            </Table.Td>
            <Table.Td w="1%">
              <Copy value={form.text} label={form.form} />
            </Table.Td>
            <Table.Td w="1%" style={{ whiteSpace: "nowrap" }}>
              <Text size="sm" c="dimmed">{form.characters}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" ff="monospace" style={{ overflowWrap: "anywhere" }} c={form.same ? "dimmed" : undefined}>
                {form.same ? "unchanged" : form.text}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

// One row per code point, and the row is the control: clicking it is what the three cards below are about, which is
// why a table of five hundred rows is bounded and scrolls rather than pushing them off the end of the page. Only the
// selected row is a tab stop and the arrow keys move between them, which is the one arrangement that reaches every
// row from the keyboard without putting five hundred stops between this card and the ones under it.
function Characters(
  { characters, nameOf, at, onPick }: {
    characters: Character[];
    nameOf: (code: number) => string;
    at: number;
    onPick: (at: number) => void;
  },
) {
  if (characters.length === 0) return <Text size="sm" c="dimmed">Nothing to read yet.</Text>;

  return (
    <Table.ScrollContainer minWidth={560} maxHeight={420} type="native">
      <Table verticalSpacing={6} horizontalSpacing="xs" highlightOnHover stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w="1%">Character</Table.Th>
            <Table.Th w="1%">Code point</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th w="1%">Category</Table.Th>
            <Table.Th w="1%">Script</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {characters.slice(0, MAX_ROWS).map((character, index) => (
            <Table.Tr
              key={`${character.at}-${character.code}`}
              className="character-row"
              tabIndex={index === at ? 0 : -1}
              aria-selected={index === at}
              onClick={() => onPick(index)}
              onKeyDown={(event) => walk(event, index, onPick)}
              data-selected={index === at || undefined}
              data-code={codePoint(character.code)}
            >
              <Table.Td>
                <Text size="sm" ff="monospace" c={isInvisible(character) ? "dimmed" : undefined}>
                  {isInvisible(character) ? placeholder(character) : character.text}
                </Text>
              </Table.Td>
              <Table.Td style={{ whiteSpace: "nowrap" }}>
                <Text size="sm" ff="monospace">{codePoint(character.code)}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" style={{ overflowWrap: "anywhere" }}>{nameOf(character.code)}</Text>
              </Table.Td>
              <Table.Td style={{ whiteSpace: "nowrap" }}>
                <Text size="sm" c="dimmed">{character.category}</Text>
              </Table.Td>
              <Table.Td style={{ whiteSpace: "nowrap" }}>
                <Text size="sm" c="dimmed">{character.script}</Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

// Selection follows focus, so a row reached with an arrow key is the row the cards below are about — and the focus
// moves with it, since a table this long is scrolled by whatever the browser is keeping in view.
function walk(event: KeyboardEvent<HTMLTableRowElement>, index: number, onPick: (at: number) => void) {
  const move = MOVES[event.key];
  if (!move) return;

  const rows = event.currentTarget.parentElement?.children;
  const to = move(index, rows?.length ?? 0);
  const row = rows?.[to];
  if (!(row instanceof HTMLElement)) return;
  event.preventDefault();
  onPick(to);
  row.focus();
}

const MOVES: Record<string, ((index: number, rows: number) => number) | undefined> = {
  ArrowDown: (index) => index + 1,
  ArrowUp: (index) => index - 1,
  Home: () => 0,
  End: (_index, rows) => rows - 1,
};

// The character at the size it was drawn to be read at, or what it is called where there is nothing to draw.
function Glyph({ character }: { character: Character }) {
  if (isInvisible(character)) {
    return (
      <Text fz={20} ff="monospace" c="dimmed" ta="center" w={96} style={{ lineHeight: "96px" }}>
        {placeholder(character)}
      </Text>
    );
  }
  return <Text fz={64} ta="center" w={96} style={{ lineHeight: "96px" }}>{character.text}</Text>;
}

function Copy({ value, label }: { value: string; label: string }) {
  return (
    <CopyButton value={value} timeout={2000}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="left">
          <ActionIcon
            color={copied ? "teal" : "gray"}
            variant="subtle"
            size="sm"
            onClick={copy}
            aria-label={`Copy ${label}`}
          >
            {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}

// What the text is, as against what any one character in it is: the two counts that differ from each other for an
// emoji and an accent written after its letter, and the two sizes it takes in the two encodings anybody stores it in.
function counts(text: string, characters: Character[]): Fact[] {
  const clusters = graphemes(text).length;
  return [
    { label: "Code points", value: String(characters.length) },
    // Worth a row only where the two disagree, which is a flag, a family, or a letter with its accent after it.
    { label: "Graphemes", value: clusters === characters.length ? "" : String(clusters) },
    { label: "UTF-16 units", value: String(text.length) },
    { label: "UTF-8 bytes", value: String(new TextEncoder().encode(text).length) },
    { label: "Scripts", value: [...new Set(characters.map(({ script }) => script))].join(", ") },
    { label: "Blocks", value: [...new Set(characters.map(({ block }) => block))].join(", ") },
  ];
}

// Who the character is, which is the half of it that is Unicode's own answer rather than arithmetic on the number.
// The last three rows are the browser's own reading of the same character, and each is drawn only where it says
// something the row above did not: a letter that is already its own uppercase has no uppercase worth a line.
function identity(character: Character, name: string): Fact[] {
  const { text } = character;
  const decomposed = text.normalize("NFD");
  const compatibility = text.normalize("NFKD");

  return [
    { label: "Name", value: name },
    { label: "Abbreviation", value: character.abbreviation },
    { label: "Category", value: `${categoryName(character.category)} (${character.category})` },
    { label: "Script", value: character.script },
    { label: "Block", value: character.block },
    { label: "Since", value: character.age === "" ? "" : `Unicode ${character.age}` },
    { label: "Looks like", value: character.looksLike === "" ? "" : `${character.looksLike} in ASCII` },
    { label: "Uppercase", value: text.toUpperCase() === text ? "" : text.toUpperCase() },
    { label: "Lowercase", value: text.toLowerCase() === text ? "" : text.toLowerCase() },
    { label: "Decomposes to", value: decomposed === text ? "" : writePoints(decomposed) },
    { label: "Stands for", value: compatibility === decomposed ? "" : writePoints(compatibility) },
  ];
}

// A finding names the code points it is about; past this many it names how many more there were instead.
const MAX_CODES = 24;

// What a key hands back: itself, which the box then spells whichever way it is being read.
type Typing = (key: Key) => void;

// What a key is called: the name it carries, for an emoji whose code points have none between them, and otherwise
// the standard's own name for the one code point it is — which is absent until the file holding it has been read.
function keyName(key: Key, nameOf: (code: number) => string): string {
  const named = key.name || (key.code === null ? "" : nameOf(key.code));
  return [writePoints(key.text), named].filter(Boolean).join(" ");
}

// A keyboard nobody has opened has no block and so no keys, and one object rather than a new one every render.
const EMPTY_KEYS: Keys = { keys: [], total: 0, sifted: false };

// Where a typed character goes: into the selection while the box has one, and where the box last left the caret
// once it has not — or onto the end, for a box nobody has put a caret in at all.
function selection(box: HTMLTextAreaElement | null, held: [number, number] | null, length: number): [number, number] {
  if (box && document.activeElement === box) return [box.selectionStart, box.selectionEnd];
  return held ?? [length, length];
}
